import React, { useState } from 'react';
import RTModal from '../common/RTModal';
import RTButton from '../common/RTButton';
import DatePicker from 'react-datepicker';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import { apiPost } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import 'react-datepicker/dist/react-datepicker.css';
import './NoticeToVacateModal.css';

const NoticeToVacateModal = ({ 
  isOpen, 
  onClose, 
  allocation, 
  onSuccess 
}) => {
  const [noticeDate, setNoticeDate] = useState(new Date());
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date;
  });
  const [loading, setLoading] = useState(false);

  // Update end date when notice date changes (30 days from notice date)
  const handleNoticeDateChange = (date) => {
    setNoticeDate(date);
    if (date) {
      const calculatedEndDate = new Date(date);
      calculatedEndDate.setDate(calculatedEndDate.getDate() + 30);
      setEndDate(calculatedEndDate);
    }
  };

  // Calculate notice period in days (remaining days from today to end date)
  const getNoticePeriodDays = () => {
    if (!endDate) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for accurate calculation
    const endDateOnly = new Date(endDate);
    endDateOnly.setHours(0, 0, 0, 0); // Set to start of day for accurate calculation
    
    const timeDifference = endDateOnly.getTime() - today.getTime();
    const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
    
    return Math.max(0, daysDifference); // Return 0 if end date is in the past
  };

  const handleConfirm = async () => {
    try {
      setLoading(true);

      const updateData = {
        notice_date: noticeDate.toISOString().split('T')[0], // Format as YYYY-MM-DD
        end_date: endDate.toISOString().split('T')[0] // Format as YYYY-MM-DD
      };

      const response = await apiPost(`/allocation/update/${allocation.allocation_id}`, updateData);
      
      if (response.status === 200) {
        toastSuccess(`Notice to vacate issued successfully for ${allocation.pg_name}`);
        onSuccess(); // Refresh the data in parent component
        onClose(); // Close modal
      }
    } catch (error) {
      console.error('Error issuing notice to vacate:', error);
      toastError(error.response?.data?.detail || 'Failed to issue notice to vacate');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form when cancelled
    setNoticeDate(new Date());
    const defaultEndDate = new Date();
    defaultEndDate.setDate(defaultEndDate.getDate() + 30);
    setEndDate(defaultEndDate);
    onClose();
  };

  const footer = (
    <>
      <RTButton
        variant="secondary"
        onClick={handleCancel}
        disabled={loading}
      >
        Cancel
      </RTButton>
      <RTButton
        variant="primary"
        onClick={handleConfirm}
        disabled={loading}
      >
        {loading ? 'Processing...' : 'Confirm Notice'}
      </RTButton>
    </>
  );

  return (
    <RTModal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Issue Notice To Vacate"
      footer={footer}
      size="medium"
    >
      <div className="notice-modal-content">
        <div className="notice-info">
          <h4>Allocation Details</h4>
          <div className="notice-info-grid">
            <div className="notice-info-item">
              <label>PG Name:</label>
              <span>{allocation?.pg_name || 'N/A'}</span>
            </div>
            <div className="notice-info-item">
              <label>Building:</label>
              <span>{allocation?.building_name || 'N/A'}</span>
            </div>
            <div className="notice-info-item">
              <label>Room:</label>
              <span>{allocation?.room_type || 'N/A'}</span>
            </div>
            <div className="notice-info-item">
              <label>Current Rent:</label>
              <span>₹{allocation?.rent_amount?.toLocaleString() || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="notice-form">
          <h4>Notice Information</h4>
          
          <div className="rt-form-group">
            <label htmlFor="notice-date">
              Notice Date <span style={{ color: 'red' }}>*</span>
            </label>
            <DatePicker
              id="notice-date"
              selected={noticeDate}
              onChange={handleNoticeDateChange}
              dateFormat="dd/MM/yyyy"
              className="rt-input custom-datepicker"
              minDate={(() => {
                const minDate = new Date();
                minDate.setDate(minDate.getDate() - 30);
                return minDate;
              })()} // Notice date can be up to 30 days in the past
              maxDate={new Date()} // Notice date cannot be in the future
              placeholderText="DD/MM/YYYY"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              autoComplete="off"
            />
            <small className="form-help-text">
              The date when the notice is issued to the paying guest (can be up to 30 days in the past)
            </small>
          </div>

          <div className="rt-form-group">
            <label htmlFor="end-date">
              Vacate By Date
            </label>
            <DatePicker
              id="end-date"
              selected={endDate}
              onChange={setEndDate}
              dateFormat="dd/MM/yyyy"
              className="rt-input custom-datepicker"
              minDate={noticeDate ? new Date(noticeDate.getTime() + 24 * 60 * 60 * 1000) : new Date()} // At least 1 day after notice date
              placeholderText="DD/MM/YYYY"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              autoComplete="off"
              disabled={true}
            />
            <small className="form-help-text">
              The date by which the paying guest must vacate (automatically calculated as 30 days from notice date)
            </small>
          </div>

          <div className="notice-summary">
            <div className="summary-item">
              <strong>Notice Period (Days Remaining):</strong> {getNoticePeriodDays()} days
            </div>
            <div className="summary-item">
              <strong>Notice Date:</strong> {formatDateToDDMMYYYY(noticeDate)}
            </div>
            <div className="summary-item">
              <strong>Vacate By:</strong> {formatDateToDDMMYYYY(endDate)}
            </div>
          </div>
        </div>
      </div>
    </RTModal>
  );
};

export default NoticeToVacateModal;
